import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { api, fetchCompanyOptions } from '../lib/api.js';
import { Modal } from './Modal.js';
import { InlinePersonnelForm, Personnel } from './InlinePersonnelForm.js';
import { InlineEquipmentTypeForm } from './InlineEquipmentTypeForm.js';
import { InlineTestMethodForm } from './InlineTestMethodForm.js';
import { AsyncSearchableSelect } from './AsyncSearchableSelect.js';
import { SearchableSelect } from './SearchableSelect.js';
import {
  buildEquipmentTypeOptions,
  getEquipmentRule,
  generateSmartShortCodeCandidates,
} from '@gng-ehs/shared';
import {
  DEFAULT_TEST_METHOD,
  DEFAULT_TEST_METHODS,
  buildTestMethodOptions,
} from '@gng-ehs/shared/equipment-rules';

export interface CompanyOption {
  id: string;
  short_code: string;
  company_name: string;
  occupier_name?: string;
  factory_address?: string;
  license_number?: string;
  registration_number?: string;
  contact_email?: string;
  communication_email?: string;
}

export interface InspectorOption {
  id: string;
  full_name: string;
  designation?: string;
  role?: string;
}

export interface EquipmentTypeOption {
  value: string;
  label: string;
  short_code: string;
}

export interface ParsedForm10Row {
  id: string; // internal UI key
  // Company
  company_input: string; // raw excel value
  company_id: string;
  has_multiple_company_matches?: boolean;
  matching_companies_count?: number;
  matching_company_names?: string[];

  // Equipment Type
  equipment_type_input: string; // raw excel value
  equipment_type: string; // key (e.g. STACKER, BOPT, CRANE_10)
  equipment_type_label: string;

  // Equipment Specs
  equipment_short_code: string; // tag name (e.g. SPL/BOPT/01)
  swl_value?: string;
  lift_height?: string;
  make?: string;
  model?: string;
  equipment_size?: string;
  location_in_factory?: string;
  first_use_type: 'EXACT_DATE' | 'YEAR_ONLY' | 'NOT_APPLICABLE' | 'NOT_KNOWN';
  first_use_value: string;

  // Certification / Form 10 Fields
  personnel_input: string; // raw excel inspector value
  personnel_id: string; // resolved competent person ID
  examination_date: string; // YYYY-MM-DD
  test_method_input?: string; // raw excel test method value
  test_method: string; // resolved test method (defaults to DEFAULT_TEST_METHOD)
  defect_status: 'SAFE' | 'DEFECT_FOUND' | 'REJECT' | 'NOT_APPLICABLE';
  defect_remarks?: string;
  annealing_details?: string;
  previous_inspection_details?: string;

  // Validation Status
  status: 'VALID' | 'MISSING_COMPANY' | 'MISSING_EQUIPMENT_TYPE' | 'MISSING_INSPECTOR' | 'INVALID';
  status_message: string;
}

export function generateForm10ExcelTemplate(
  companies: CompanyOption[],
  equipmentTypes: EquipmentTypeOption[],
  inspectors: InspectorOption[],
  testMethods: readonly string[] | string[] = DEFAULT_TEST_METHODS
) {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Data Entry Template
  const templateHeaders = [
    'Company (Short Code or Name) *',
    'Equipment Type (Name or Code) *',
    'Tag Name / Code (Optional)',
    'SWL in Ton (Optional)',
    'Lift Height (Optional)',
    'Make (Optional)',
    'Model (Optional)',
    'Equipment Size (Optional)',
    'Location in Factory (Optional)',
    'First Use Date/Year (Optional)',
    'Competent Person / Inspector Name *',
    'Examination Date (DD-MM-YYYY) *',
    'Test Method (Point 5) (Optional)',
    'Defect Status (Safe / Reject / Not Applicable)',
    'Defect Remarks (Point 8)',
    'Annealing Details (Point 7)',
    'Previous Inspection Details (Point 6)',
  ];

  const wsData = [templateHeaders];
  const ws1 = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws1['!cols'] = [
    { wch: 35 }, // Company
    { wch: 32 }, // Equipment Type
    { wch: 24 }, // Tag Name
    { wch: 18 }, // SWL
    { wch: 18 }, // Lift Height
    { wch: 20 }, // Make
    { wch: 20 }, // Model
    { wch: 20 }, // Equipment Size
    { wch: 24 }, // Location
    { wch: 24 }, // First Use
    { wch: 32 }, // Competent Person
    { wch: 26 }, // Exam Date
    { wch: 36 }, // Test Method (Point 5)
    { wch: 22 }, // Defect Status
    { wch: 35 }, // Defect Remarks
    { wch: 28 }, // Annealing
    { wch: 32 }, // Prev Inspection
  ];

  // Sheet 2: Lookups & Instructions
  const lookupHeaders = [
    'Company Short Code',
    'Company Full Name',
    ' ',
    'Equipment Type Name',
    'Equipment Short Code',
    ' ',
    'Competent Person / Inspector Name',
    ' ',
    'Defect Status Options',
    ' ',
    'Test Method Options',
  ];

  const defectStatuses = ['Safe', 'Reject', 'Not Applicable'];

  const lookupRows: (string | number)[][] = [];
  const maxLen = Math.max(companies.length, equipmentTypes.length, inspectors.length, defectStatuses.length, testMethods.length, 10);
  for (let i = 0; i < maxLen; i++) {
    const c = companies[i];
    const e = equipmentTypes[i];
    const ins = inspectors[i];
    const d = defectStatuses[i];
    const tm = testMethods[i];
    lookupRows.push([
      c ? c.short_code : '',
      c ? c.company_name : '',
      ' ',
      e ? e.label : '',
      e ? e.short_code : '',
      ' ',
      ins ? ins.full_name : '',
      ' ',
      d || '',
      ' ',
      tm || '',
    ]);
  }

  const instructions = [
    ['INSTRUCTIONS & GUIDE FOR BULK IMPORTING FORM 10 CERTIFICATES:'],
    ['1. FILLING COMPANY:'],
    ['   - Enter Company Short Code (e.g. SPS) or Company Name in Column A.'],
    ['   - Or click cell in Column A to select from available companies in the dropdown.'],
    ['2. FILLING EQUIPMENT TYPE:'],
    ['   - Enter Equipment Name (e.g. Stacker, BOPT, Crane) or Short Code (e.g. STACKER, BOPT, CRANE_10) in Column B.'],
    ['3. TAG NAME (OPTIONAL):'],
    ['   - If left blank, the system will automatically generate a sequential Tag Name (e.g. SPL/BOPT/01).'],
    ['4. COMPETENT PERSON / INSPECTOR:'],
    ['   - Select or enter the Competent Person full name in Column K.'],
    ['5. EXAMINATION DATE:'],
    ['   - Standard DD-MM-YYYY (e.g. 15-08-2026) or standard Excel date format in Column L.'],
    ['6. TEST METHOD (POINT 5) (OPTIONAL):'],
    ['   - Enter or select Test Method in Column M (e.g. "Visual inspection & load test", "Visual inspection & dynamic load test", etc.).'],
    ['   - Defaults to "Visual inspection & load test" if left blank.'],
    ['7. DEFECT STATUS (OPTIONAL):'],
    ['   - Enter "Safe", "Reject", or "Not Applicable" in Column N (defaults to "Safe" if blank).'],
    ['   - If "Reject", please provide defect details in Defect Remarks (Column O).'],
    [''],
    ['--- CURRENT SYSTEM LOOKUP DATA ---'],
    lookupHeaders,
    ...lookupRows,
  ];

  const ws2 = XLSX.utils.aoa_to_sheet(instructions);
  ws2['!cols'] = [{ wch: 22 }, { wch: 35 }, { wch: 4 }, { wch: 30 }, { wch: 20 }, { wch: 4 }, { wch: 32 }, { wch: 4 }, { wch: 22 }, { wch: 4 }, { wch: 40 }];

  // SheetJS Data Validation lists for native Excel dropdowns
  const companyCount = Math.max(companies.length, 1);
  const equipCount = Math.max(equipmentTypes.length, 1);
  const inspectorCount = Math.max(inspectors.length, 1);
  const testMethodCount = Math.max(testMethods.length, 1);

  ws1['!dataValidation'] = [
    {
      sqref: 'A2:A500', // Company
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$A$16:$A$${15 + companyCount}`,
      allowBlank: true,
    },
    {
      sqref: 'B2:B500', // Equipment Type
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$D$16:$D$${15 + equipCount}`,
      allowBlank: true,
    },
    {
      sqref: 'K2:K500', // Competent Person
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$G$16:$G$${15 + inspectorCount}`,
      allowBlank: true,
    },
    {
      sqref: 'M2:M500', // Test Method (Point 5)
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$K$16:$K$${15 + testMethodCount}`,
      allowBlank: true,
    },
    {
      sqref: 'N2:N500', // Defect Status
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$I$16:$I$18`,
      allowBlank: true,
    },
  ];

  XLSX.utils.book_append_sheet(wb, ws1, 'Form 10 Template');
  XLSX.utils.book_append_sheet(wb, ws2, 'Lookups');

  XLSX.writeFile(wb, `Form_10_Certificates_Import_Template.xlsx`);
}

export function Form10ExcelImportModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [customTypes, setCustomTypes] = useState<Array<{ key: string; label: string; short_code?: string }>>([]);
  const [inspectors, setInspectors] = useState<InspectorOption[]>([]);
  const [customTestMethods, setCustomTestMethods] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [parsedRows, setParsedRows] = useState<ParsedForm10Row[]>([]);
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);

  // Quick Add Company panel state
  const [showQuickCompanyForm, setShowQuickCompanyForm] = useState(false);
  const [activeCompanyTargetRowId, setActiveCompanyTargetRowId] = useState<string | null>(null);
  const [quickCompanyName, setQuickCompanyName] = useState('');
  const [quickCompanySaving, setQuickCompanySaving] = useState(false);

  // Inline Personnel Form state for adding new Inspector
  const [showInlineInspectorForm, setShowInlineInspectorForm] = useState(false);
  const [activeInspectorTargetRowId, setActiveInspectorTargetRowId] = useState<string | null>(null);
  const [activeInspectorInitialName, setActiveInspectorInitialName] = useState<string>('');

  // Inline Equipment Type Form state for adding new custom equipment type
  const [showInlineTypeForm, setShowInlineTypeForm] = useState(false);
  const [activeTypeTargetRowId, setActiveTypeTargetRowId] = useState<string | null>(null);

  // Inline Test Method Form state for adding new custom test method
  const [showInlineTestMethodForm, setShowInlineTestMethodForm] = useState(false);
  const [activeTestMethodTargetRowId, setActiveTestMethodTargetRowId] = useState<string | null>(null);

  // Batch actions state
  const [batchCompanyId, setBatchCompanyId] = useState('');
  const [batchEquipmentType, setBatchEquipmentType] = useState('');
  const [batchPersonnelId, setBatchPersonnelId] = useState('');
  const [batchDate, setBatchDate] = useState('');
  const [batchTestMethod, setBatchTestMethod] = useState<string>(DEFAULT_TEST_METHOD);
  const [batchDefectStatus, setBatchDefectStatus] = useState<string>('SAFE');

  // Import execution state
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number; step: string } | null>(null);
  const [error, setError] = useState('');
  const [importSuccessSummary, setImportSuccessSummary] = useState<{ count: number } | null>(null);

  const allTypeOptions = useMemo(() => {
    return buildEquipmentTypeOptions(customTypes);
  }, [customTypes]);

  const allTestMethodOptions = useMemo(() => {
    return buildTestMethodOptions(customTestMethods);
  }, [customTestMethods]);

  // Load companies, custom types, competent persons, and test methods on open
  useEffect(() => {
    if (!open) return;
    setLoadingLookups(true);

    Promise.all([
      api.companies.list({ limit: 1000 }),
      api.equipmentTypes.list(),
      api.personnel.list({ limit: 1000 }),
      api.testMethods.list(),
    ])
      .then(([compRes, typesRes, persRes, tmRes]) => {
        const compList = (compRes.data ?? []) as CompanyOption[];
        setCompanies(compList);

        if (typesRes.data) {
          setCustomTypes(typesRes.data.map((t) => ({ key: t.key, label: t.label, short_code: t.short_code })));
        }

        const persList = (persRes.data ?? []) as InspectorOption[];
        setInspectors(persList);

        if (tmRes.data) {
          setCustomTestMethods(tmRes.data);
        }
      })
      .catch((err) => {
        console.error('Failed to load lookup data:', err);
      })
      .finally(() => {
        setLoadingLookups(false);
      });
  }, [open]);

  // Robust Excel date parser
  const normalizeDateStr = (raw: unknown): string => {
    if (raw === undefined || raw === null || raw === '') return new Date().toISOString().split('T')[0];

    const numVal = typeof raw === 'number' ? raw : (typeof raw === 'string' && /^\d{5}(\.\d+)?$/.test(raw.trim()) ? Number(raw.trim()) : NaN);
    if (!isNaN(numVal) && numVal > 25000 && numVal < 75000) {
      const parsedDate = XLSX.SSF.parse_date_code(numVal);
      if (parsedDate) {
        const yyyy = String(parsedDate.y).padStart(4, '0');
        const mm = String(parsedDate.m).padStart(2, '0');
        const dd = String(parsedDate.d).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    const ddmmyyyy = s.match(/^(\d{1,2})[/\-.]( \d{1,2})[/\-.]( \d{4})$/);
    if (ddmmyyyy) {
      const dd = String(ddmmyyyy[1]).padStart(2, '0');
      const mm = String(ddmmyyyy[2]).padStart(2, '0');
      const yyyy = ddmmyyyy[3];
      return `${yyyy}-${mm}-${dd}`;
    }

    const yyyymmdd = s.match(/^(\d{4})[/\-.]( \d{1,2})[/\-.]( \d{1,2})$/);
    if (yyyymmdd) {
      const yyyy = yyyymmdd[1];
      const mm = String(yyyymmdd[2]).padStart(2, '0');
      const dd = String(yyyymmdd[3]).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    return new Date().toISOString().split('T')[0];
  };

  // Helper entity matcher
  const matchRowData = (
    rawCompany: string,
    rawEquipType: string,
    rawInspector: string,
    allCompanies: CompanyOption[],
    typeOptions: EquipmentTypeOption[],
    allInspectors: InspectorOption[]
  ) => {
    // 1. Match Company (handles exact match, code, name, composite strings, and partial match)
    const compClean = rawCompany.trim();
    let company_id = '';
    let has_multiple_company_matches = false;
    let matching_companies_count = 0;
    let matching_company_names: string[] = [];

    if (compClean) {
      const compCleanLower = compClean.toLowerCase();

      // Strong / Direct matches (exact code, exact name, or standard composite formats)
      const exactMatches = allCompanies.filter((c) => {
        const cName = (c.company_name || '').trim().toLowerCase();
        const cCode = (c.short_code || '').trim().toLowerCase();
        return (
          (cCode && cCode === compCleanLower) ||
          (cName && cName === compCleanLower) ||
          (cCode && cName && `${cCode} - ${cName}` === compCleanLower) ||
          (cCode && cName && `${cName} - ${cCode}` === compCleanLower) ||
          (cCode && cName && `${cName} [${cCode}]` === compCleanLower) ||
          (cCode && cName && `${cCode} [${cName}]` === compCleanLower) ||
          (cCode && compCleanLower.startsWith(`${cCode} -`)) ||
          (cName && compCleanLower.startsWith(`${cName} -`)) ||
          (cName && compCleanLower.startsWith(`${cName} [`)) ||
          (cCode && compCleanLower.startsWith(`${cCode} [`))
        );
      });

      let compMatches = exactMatches;

      // Fallback: If no exact/strong match, check substring / contains match
      if (compMatches.length === 0) {
        compMatches = allCompanies.filter((c) => {
          const cName = (c.company_name || '').trim().toLowerCase();
          const cCode = (c.short_code || '').trim().toLowerCase();
          return (
            (cName && (cName.includes(compCleanLower) || (compCleanLower.length >= 3 && compCleanLower.includes(cName)))) ||
            (cCode && cCode.includes(compCleanLower))
          );
        });
      }

      if (compMatches.length > 0) {
        company_id = compMatches[0].id;
        if (compMatches.length > 1) {
          has_multiple_company_matches = true;
          matching_companies_count = compMatches.length;
          matching_company_names = compMatches.map((m) =>
            m.short_code ? `${m.short_code} - ${m.company_name}` : m.company_name
          );
        }
      }
    }

    // 2. Match Equipment Type
    const typeClean = rawEquipType.trim();
    let equipment_type = '';
    let equipment_type_label = '';

    if (typeClean) {
      const typeCleanUpper = typeClean.toUpperCase();

      // Check against built-in & custom type options
      const foundOpt = typeOptions.find(
        (o) =>
          o.value.toUpperCase() === typeCleanUpper ||
          o.short_code.toUpperCase() === typeCleanUpper ||
          o.label.toUpperCase() === typeCleanUpper ||
          o.label.toUpperCase().startsWith(typeCleanUpper)
      );

      if (foundOpt) {
        equipment_type = foundOpt.value;
        equipment_type_label = foundOpt.label;
      } else {
        // Fallback to getEquipmentRule
        const rule = getEquipmentRule(typeClean);
        if (rule) {
          equipment_type = rule.key;
          equipment_type_label = rule.label;
        }
      }
    }

    // 3. Match Competent Person / Inspector
    const insClean = rawInspector.trim();
    let personnel_id = '';
    if (insClean) {
      const foundIns = allInspectors.find(
        (ins) =>
          ins.full_name.toLowerCase() === insClean.toLowerCase() ||
          insClean.toLowerCase().includes(ins.full_name.toLowerCase()) ||
          ins.full_name.toLowerCase().includes(insClean.toLowerCase())
      );
      if (foundIns) {
        personnel_id = foundIns.id;
      }
    }

    return {
      company_id,
      has_multiple_company_matches,
      matching_companies_count,
      matching_company_names,
      equipment_type,
      equipment_type_label,
      personnel_id,
    };
  };

  // File Upload Handler
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    setError('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });

        const sheetName =
          wb.SheetNames.find((n) => n.includes('Template') || n.includes('Form 10') || n.includes('Certificates') || n.includes('Sheet1')) ||
          wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];

        const rawData = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });

        if (rawData.length === 0) {
          setError('The uploaded Excel file contains no data rows.');
          setParsing(false);
          return;
        }

        const rows: ParsedForm10Row[] = [];

        rawData.forEach((rowObj, index) => {
          const keys = Object.keys(rowObj);
          const getRawVal = (matches: string[]) => {
            const k = keys.find((key) => matches.some((m) => key.toLowerCase().trim().includes(m.toLowerCase())));
            return k ? rowObj[k] : undefined;
          };
          const getKeyVal = (matches: string[]) => {
            const val = getRawVal(matches);
            return val !== undefined && val !== null ? String(val).trim() : '';
          };

          const company_input = getKeyVal(['company', 'client', 'organisation']);
          const equipment_type_input = getKeyVal(['equipment type', 'type', 'machine type', 'equipment name']);
          const equipment_short_code = getKeyVal(['tag name', 'tag', 'short code', 'equipment code', 'identification']);
          const swl_value = getKeyVal(['swl', 'safe working load', 'capacity', 'ton']);
          const lift_height = getKeyVal(['lift height', 'height', 'lift']);
          const make = getKeyVal(['make', 'manufacturer']);
          const model = getKeyVal(['model', 'model no']);
          const equipment_size = getKeyVal(['size', 'equipment size', 'dimension']);
          const location_in_factory = getKeyVal(['location', 'location in factory', 'dept', 'department']);

          const first_use_raw = getKeyVal(['first use', 'date of first use', 'first used']);
          let first_use_type: 'EXACT_DATE' | 'YEAR_ONLY' | 'NOT_APPLICABLE' | 'NOT_KNOWN' = 'NOT_APPLICABLE';
          let first_use_value = 'Not Applicable';

          if (first_use_raw) {
            const fuLower = first_use_raw.toLowerCase();
            if (fuLower.includes('not known') || fuLower === 'unknown') {
              first_use_type = 'NOT_KNOWN';
              first_use_value = 'Not Known';
            } else if (fuLower.includes('not applicable') || fuLower === 'na' || fuLower === 'n/a') {
              first_use_type = 'NOT_APPLICABLE';
              first_use_value = 'Not Applicable';
            } else if (/^\d{4}$/.test(first_use_raw.trim())) {
              first_use_type = 'YEAR_ONLY';
              first_use_value = first_use_raw.trim();
            } else {
              first_use_type = 'EXACT_DATE';
              first_use_value = normalizeDateStr(first_use_raw);
            }
          }

          const personnel_input = getKeyVal(['competent person', 'inspector', 'personnel', 'examiner']);
          const rawDate = getRawVal(['examination date', 'date of exam', 'exam date', 'test date', 'date']);
          const test_method_raw = getKeyVal(['test method', 'test_method', 'method of test', 'method', 'point 5', 'testing method', 'inspection method']);
          let test_method = DEFAULT_TEST_METHOD;
          if (test_method_raw) {
            const matchedTm = allTestMethodOptions.find((m) => m.value.toLowerCase() === test_method_raw.toLowerCase());
            test_method = matchedTm ? matchedTm.value : test_method_raw;
          }
          const rawDefectStatus = getKeyVal(['defect status', 'status', 'condition']).toLowerCase();

          let defect_status: 'SAFE' | 'DEFECT_FOUND' | 'REJECT' | 'NOT_APPLICABLE' = 'SAFE';
          if (rawDefectStatus.includes('reject') || rawDefectStatus.includes('defect') || rawDefectStatus.includes('unsafe') || rawDefectStatus.includes('repair')) {
            defect_status = 'REJECT';
          } else if (rawDefectStatus.includes('not applicable') || rawDefectStatus === 'n/a' || rawDefectStatus === 'na') {
            defect_status = 'NOT_APPLICABLE';
          }

          const defect_remarks = getKeyVal(['defect remarks', 'defect', 'remarks', 'particulars of defect', 'point 8']);
          const annealing_details = getKeyVal(['annealing', 'heat treatment', 'point 7']) || 'Not Applicable';
          const previous_inspection_details = getKeyVal(['previous inspection', 'prev inspection', 'point 6']) || 'Not Applicable';

          // Skip completely blank rows
          if (!company_input && !equipment_type_input && rawDate === undefined) return;

          const examination_date = normalizeDateStr(rawDate);
          const matched = matchRowData(company_input, equipment_type_input, personnel_input, companies, allTypeOptions, inspectors);

          let status: 'VALID' | 'MISSING_COMPANY' | 'MISSING_EQUIPMENT_TYPE' | 'MISSING_INSPECTOR' | 'INVALID' = 'VALID';
          let status_message = 'Ready to import';

          if (!matched.company_id) {
            status = 'MISSING_COMPANY';
            status_message = company_input ? `Unmatched Company "${company_input}"` : 'Missing Company';
          } else if (!matched.equipment_type) {
            status = 'MISSING_EQUIPMENT_TYPE';
            status_message = equipment_type_input ? `Unmatched Type "${equipment_type_input}"` : 'Missing Equipment Type';
          } else if (!matched.personnel_id) {
            status = 'MISSING_INSPECTOR';
            status_message = personnel_input ? `Unmatched Inspector "${personnel_input}"` : 'Missing Competent Person';
          } else if (!examination_date) {
            status = 'INVALID';
            status_message = 'Missing Examination Date';
          }

          rows.push({
            id: `row_${index}_${Math.random().toString(36).substring(2, 7)}`,
            company_input,
            company_id: matched.company_id,
            has_multiple_company_matches: matched.has_multiple_company_matches,
            matching_companies_count: matched.matching_companies_count,
            matching_company_names: matched.matching_company_names,
            equipment_type_input,
            equipment_type: matched.equipment_type,
            equipment_type_label: matched.equipment_type_label,
            equipment_short_code,
            swl_value,
            lift_height,
            make,
            model,
            equipment_size,
            location_in_factory,
            first_use_type,
            first_use_value,
            personnel_input,
            personnel_id: matched.personnel_id,
            examination_date,
            test_method_input: test_method_raw,
            test_method,
            defect_status,
            defect_remarks,
            annealing_details,
            previous_inspection_details,
            status,
            status_message,
          });
        });

        if (rows.length === 0) {
          setError('Could not find valid certificate rows in the file. Please use our downloadable sample Excel template.');
        } else {
          setParsedRows(rows);
        }
      } catch (err: unknown) {
        setError(`Failed to parse Excel file: ${err instanceof Error ? err.message : 'Unknown format'}`);
      } finally {
        setParsing(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  // Row update with smart propagation for identical missing inputs
  const updateRow = (id: string, updates: Partial<ParsedForm10Row>) => {
    setParsedRows((prev) => {
      const targetRow = prev.find((r) => r.id === id);
      const targetCompanyInput = targetRow?.company_input?.trim().toLowerCase();
      const targetTypeInput = targetRow?.equipment_type_input?.trim().toLowerCase();
      const targetPersonnelInput = targetRow?.personnel_input?.trim().toLowerCase();
      const targetTestMethodInput = targetRow?.test_method_input?.trim().toLowerCase();

      return prev.map((r) => {
        const isTarget = r.id === id;
        const isMatchingSameCompany =
          !isTarget &&
          updates.company_id !== undefined &&
          targetCompanyInput &&
          r.company_input &&
          r.company_input.trim().toLowerCase() === targetCompanyInput;

        const isMatchingSameType =
          !isTarget &&
          updates.equipment_type !== undefined &&
          targetTypeInput &&
          r.equipment_type_input &&
          r.equipment_type_input.trim().toLowerCase() === targetTypeInput;

        const isMatchingSamePersonnel =
          !isTarget &&
          updates.personnel_id !== undefined &&
          targetPersonnelInput &&
          r.personnel_input &&
          r.personnel_input.trim().toLowerCase() === targetPersonnelInput;

        const isMatchingSameTestMethod =
          !isTarget &&
          updates.test_method !== undefined &&
          targetTestMethodInput &&
          r.test_method_input &&
          r.test_method_input.trim().toLowerCase() === targetTestMethodInput;

        if (!isTarget && !isMatchingSameCompany && !isMatchingSameType && !isMatchingSamePersonnel && !isMatchingSameTestMethod) {
          return r;
        }

        const updated = {
          ...r,
          ...updates,
          ...(updates.company_id !== undefined ? { has_multiple_company_matches: false } : {}),
        };

        // Recompute status
        if (!updated.company_id) {
          updated.status = 'MISSING_COMPANY';
          updated.status_message = updated.company_input ? `Unmatched Company "${updated.company_input}"` : 'Missing Company';
        } else if (!updated.equipment_type) {
          updated.status = 'MISSING_EQUIPMENT_TYPE';
          updated.status_message = updated.equipment_type_input ? `Unmatched Type "${updated.equipment_type_input}"` : 'Missing Equipment Type';
        } else if (!updated.personnel_id) {
          updated.status = 'MISSING_INSPECTOR';
          updated.status_message = updated.personnel_input ? `Unmatched Inspector "${updated.personnel_input}"` : 'Missing Competent Person';
        } else if (!updated.examination_date) {
          updated.status = 'INVALID';
          updated.status_message = 'Missing Examination Date';
        } else {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }

        return updated;
      });
    });
  };

  const removeRow = (id: string) => {
    setParsedRows((prev) => prev.filter((r) => r.id !== id));
  };

  // Batch actions
  const applyBatchCompany = () => {
    if (!batchCompanyId) return;
    setParsedRows((prev) =>
      prev.map((r) => {
        const updated = { ...r, company_id: batchCompanyId, has_multiple_company_matches: false };
        if (updated.equipment_type && updated.personnel_id && updated.examination_date) {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }
        return updated;
      })
    );
  };

  const applyBatchEquipmentType = () => {
    if (!batchEquipmentType) return;
    const foundOpt = allTypeOptions.find((o) => o.value === batchEquipmentType);
    setParsedRows((prev) =>
      prev.map((r) => {
        const updated = {
          ...r,
          equipment_type: batchEquipmentType,
          equipment_type_label: foundOpt?.label || batchEquipmentType,
        };
        if (updated.company_id && updated.personnel_id && updated.examination_date) {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }
        return updated;
      })
    );
  };

  const applyBatchPersonnel = () => {
    if (!batchPersonnelId) return;
    setParsedRows((prev) =>
      prev.map((r) => {
        const updated = { ...r, personnel_id: batchPersonnelId };
        if (updated.company_id && updated.equipment_type && updated.examination_date) {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }
        return updated;
      })
    );
  };

  const applyBatchDate = () => {
    if (!batchDate) return;
    setParsedRows((prev) =>
      prev.map((r) => ({
        ...r,
        examination_date: batchDate,
      }))
    );
  };

  const applyBatchTestMethod = () => {
    if (!batchTestMethod) return;
    setParsedRows((prev) =>
      prev.map((r) => ({
        ...r,
        test_method: batchTestMethod,
      }))
    );
  };

  const applyBatchDefectStatus = () => {
    if (!batchDefectStatus) return;
    setParsedRows((prev) =>
      prev.map((r) => ({
        ...r,
        defect_status: batchDefectStatus as ParsedForm10Row['defect_status'],
      }))
    );
  };

  const handleTestMethodCreated = (newMethod: { id: string; name: string }) => {
    setCustomTestMethods((prev) => [...prev, newMethod]);
    if (activeTestMethodTargetRowId) {
      updateRow(activeTestMethodTargetRowId, {
        test_method: newMethod.name,
      });
    }
    setShowInlineTestMethodForm(false);
    setActiveTestMethodTargetRowId(null);
  };

  // Quick Add Company Handler
  const handleQuickCreateCompany = async () => {
    const name = quickCompanyName.trim();
    if (!name) {
      setError('Please enter a Company Name.');
      return;
    }

    setQuickCompanySaving(true);
    setError('');

    try {
      let short_code = '';
      try {
        const suggestedRes = await api.companies.suggestShortCode(name);
        short_code = suggestedRes.data?.short_code || '';
      } catch {
        /* fallback to client matching */
      }

      if (!short_code) {
        const candidates = generateSmartShortCodeCandidates(name);
        const existingCodes = new Set(companies.map((c) => c.short_code.toUpperCase()));
        short_code = candidates.find((c) => !existingCodes.has(c.toUpperCase())) || candidates[0] || name.slice(0, 4).toUpperCase();
      }

      const createdRes = await api.companies.create({
        company_name: name,
        short_code,
        occupier_name: 'N/A',
        factory_address: 'N/A',
      });

      const newCompany = (createdRes as { data?: CompanyOption }).data;
      if (newCompany?.id) {
        setCompanies((prev) => [...prev, newCompany]);
        setShowQuickCompanyForm(false);

        const targetRow = parsedRows.find((r) => r.id === activeCompanyTargetRowId);
        const targetInput = (name || targetRow?.company_input || '').trim().toLowerCase();

        setParsedRows((prev) =>
          prev.map((r) => {
            const isMatchingRow =
              r.id === activeCompanyTargetRowId ||
              (targetInput && r.company_input && r.company_input.trim().toLowerCase() === targetInput) ||
              (r.company_input && name.toLowerCase().includes(r.company_input.trim().toLowerCase()));

            if (isMatchingRow) {
              const updated = {
                ...r,
                company_id: newCompany.id,
                company_input: `${newCompany.short_code} - ${newCompany.company_name}`,
                has_multiple_company_matches: false,
              };
              if (updated.equipment_type && updated.personnel_id && updated.examination_date) {
                updated.status = 'VALID';
                updated.status_message = 'Ready to import';
              } else if (!updated.equipment_type) {
                updated.status = 'MISSING_EQUIPMENT_TYPE';
                updated.status_message = 'Missing Equipment Type';
              } else if (!updated.personnel_id) {
                updated.status = 'MISSING_INSPECTOR';
                updated.status_message = 'Missing Competent Person';
              }
              return updated;
            }
            return r;
          })
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create company');
    } finally {
      setQuickCompanySaving(false);
    }
  };

  // Handle Created Personnel / Inspector
  const handleInspectorCreated = (newInspector: Personnel) => {
    setInspectors((prev) => [...prev, newInspector]);
    setShowInlineInspectorForm(false);

    const targetRow = parsedRows.find((r) => r.id === activeInspectorTargetRowId);
    const targetName = (targetRow?.personnel_input || newInspector.full_name).trim().toLowerCase();

    setParsedRows((prev) =>
      prev.map((r) => {
        const isMatchingRow =
          r.id === activeInspectorTargetRowId ||
          (targetName && r.personnel_input && r.personnel_input.trim().toLowerCase() === targetName) ||
          (r.personnel_input && newInspector.full_name.toLowerCase().includes(r.personnel_input.trim().toLowerCase()));

        if (isMatchingRow) {
          const updated = {
            ...r,
            personnel_id: newInspector.id,
            personnel_input: newInspector.full_name,
          };
          if (updated.company_id && updated.equipment_type && updated.examination_date) {
            updated.status = 'VALID';
            updated.status_message = 'Ready to import';
          }
          return updated;
        }
        return r;
      })
    );
    setActiveInspectorTargetRowId(null);
  };

  // Handle Created Equipment Type
  const handleTypeCreated = (newType: { key: string; label: string; short_code?: string }) => {
    setCustomTypes((prev) => [...prev, newType]);
    setShowInlineTypeForm(false);

    const targetRow = parsedRows.find((r) => r.id === activeTypeTargetRowId);
    const targetName = (targetRow?.equipment_type_input || newType.label).trim().toLowerCase();

    setParsedRows((prev) =>
      prev.map((r) => {
        const isMatchingRow =
          r.id === activeTypeTargetRowId ||
          (targetName && r.equipment_type_input && r.equipment_type_input.trim().toLowerCase() === targetName) ||
          (r.equipment_type_input && newType.label.toLowerCase().includes(r.equipment_type_input.trim().toLowerCase()));

        if (isMatchingRow) {
          const updated = {
            ...r,
            equipment_type: newType.key,
            equipment_type_label: newType.label,
            equipment_type_input: newType.label,
          };
          if (updated.company_id && updated.personnel_id && updated.examination_date) {
            updated.status = 'VALID';
            updated.status_message = 'Ready to import';
          }
          return updated;
        }
        return r;
      })
    );
    setActiveTypeTargetRowId(null);
  };

  // Perform Final Bulk Import
  const handleExecuteImport = async () => {
    const invalidRows = parsedRows.filter((r) => r.status !== 'VALID');
    if (invalidRows.length > 0) {
      setError(`Cannot proceed: ${invalidRows.length} row(s) have unresolved errors or missing fields.`);
      return;
    }

    setImporting(true);
    setError('');
    setProgress({ current: 0, total: parsedRows.length, step: 'Starting import...' });

    try {
      const createdCount = parsedRows.length;

      // Import row by row: Create equipment -> Create Form 10
      for (let i = 0; i < parsedRows.length; i++) {
        const row = parsedRows[i];
        setProgress({
          current: i + 1,
          total: parsedRows.length,
          step: `Processing certificate ${i + 1} of ${parsedRows.length} (${row.equipment_type_label || row.equipment_type})...`,
        });

        // 1. Determine Tag Name (use provided or auto-suggest)
        let finalTagName = row.equipment_short_code.trim().toUpperCase();
        if (!finalTagName) {
          try {
            const tagRes = await api.equipment.suggestTagName(row.company_id, row.equipment_type);
            finalTagName = tagRes.data?.suggested || `${row.equipment_type}-${i + 1}`;
          } catch {
            finalTagName = `${row.equipment_type}-${i + 1}`;
          }
        }

        // 2. Create Equipment Record
        const equipPayload = {
          company_id: row.company_id,
          equipment_name_type: row.equipment_type,
          equipment_short_code: finalTagName,
          swl_value: row.swl_value ? parseFloat(row.swl_value) : undefined,
          lift_height: row.lift_height?.trim() || undefined,
          make: row.make?.trim() || undefined,
          model: row.model?.trim() || undefined,
          equipment_size: row.equipment_size?.trim() || undefined,
          location_in_factory: row.location_in_factory?.trim() || undefined,
          first_use_type: row.first_use_type,
          first_use_value: row.first_use_value || 'Not Applicable',
        };

        const equipRes = await api.equipment.create(equipPayload);
        const createdEquip = (equipRes as { data?: { id: string } }).data;
        if (!createdEquip || !createdEquip.id) {
          throw new Error(`Failed to create equipment for row ${i + 1} (${finalTagName})`);
        }

        // 3. Create Form 10 Certificate
        const form10Payload = {
          company_id: row.company_id,
          equipment_id: createdEquip.id,
          personnel_id: row.personnel_id,
          examination_date: row.examination_date,
          test_method: row.test_method || DEFAULT_TEST_METHOD,
          defect_status: row.defect_status,
          defect_remarks: (row.defect_status === 'SAFE' || row.defect_status === 'NOT_APPLICABLE') ? undefined : (row.defect_remarks?.trim() || undefined),
          annealing_details: row.annealing_details?.trim() || undefined,
          previous_inspection_details: row.previous_inspection_details?.trim() || undefined,
        };

        await api.form10.create(form10Payload);
      }

      setImportSuccessSummary({ count: createdCount });
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Bulk import failed unexpectedly.');
    } finally {
      setImporting(false);
      setProgress(null);
    }
  };

  const handleResetModal = () => {
    setParsedRows([]);
    setFileName('');
    setError('');
    setImportSuccessSummary(null);
    setShowInlineInspectorForm(false);
    setShowInlineTypeForm(false);
    setShowInlineTestMethodForm(false);
    setActiveTestMethodTargetRowId(null);
    setShowQuickCompanyForm(false);
  };

  // Validation Summary & Counts
  const validationSummary = useMemo(() => {
    const missingCompany = parsedRows.filter((r) => !r.company_id).length;
    const missingType = parsedRows.filter((r) => !r.equipment_type).length;
    const missingInspector = parsedRows.filter((r) => !r.personnel_id).length;
    const missingDate = parsedRows.filter((r) => !r.examination_date).length;

    const reasons: string[] = [];
    if (missingCompany > 0) reasons.push(`${missingCompany} row(s) missing company`);
    if (missingType > 0) reasons.push(`${missingType} row(s) missing equipment type`);
    if (missingInspector > 0) reasons.push(`${missingInspector} row(s) missing inspector`);
    if (missingDate > 0) reasons.push(`${missingDate} row(s) missing date`);

    return {
      isValid: parsedRows.length > 0 && reasons.length === 0,
      reason: reasons.join(', '),
      missingCompany,
      missingType,
      missingInspector,
      missingDate,
    };
  }, [parsedRows]);

  const multipleMatchRowsCount = useMemo(() => {
    return parsedRows.filter((r) => r.has_multiple_company_matches).length;
  }, [parsedRows]);

  return (
    <Modal
      open={open}
      onClose={() => {
        if (importing) return;
        handleResetModal();
        onClose();
      }}
      title="⚡ Bulk Import Form 10 Certificates via Excel"
      size="full"
    >
      <div className="space-y-4 text-xs sm:text-sm">
        {/* Top Description & Sample Template Download Button */}
        <div className="p-3.5 bg-brand-50/70 border border-brand-200 rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div>
            <h4 className="font-bold text-brand-900 text-sm">Download Clean Excel Template with Native Dropdowns</h4>
            <p className="text-xs text-brand-700 mt-0.5">
              Download our clean Excel template featuring interactive Excel dropdowns for Companies, Equipment Types, Inspectors, and Test Methods.
            </p>
          </div>
          <button
            type="button"
            onClick={() => generateForm10ExcelTemplate(companies, allTypeOptions, inspectors, allTestMethodOptions.map((o) => o.value))}
            disabled={loadingLookups}
            className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 shrink-0 cursor-pointer transition-all"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Download Sample Excel Template
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex justify-between items-center">
            <span>{error}</span>
            <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Success Screen */}
        {importSuccessSummary ? (
          <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl text-center space-y-4 my-4">
            <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-2xl font-bold">
              ✓
            </div>
            <div>
              <h3 className="text-lg font-bold text-emerald-900">Bulk Import Completed Successfully!</h3>
              <p className="text-sm text-emerald-700 mt-1">
                Successfully created <strong>{importSuccessSummary.count} Equipment records</strong> and generated matching <strong>Form 10 Certificates</strong>.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                handleResetModal();
                onClose();
              }}
              className="btn btn-primary text-xs px-6 py-2 cursor-pointer"
            >
              Close & View Form 10 Certificates
            </button>
          </div>
        ) : (
          <>
            {/* Quick Add Company Panel */}
            {showQuickCompanyForm && (
              <div className="p-4 bg-emerald-50/90 border border-emerald-200 rounded-xl shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-emerald-800">Quick Add Company</p>
                  <button type="button" onClick={() => setShowQuickCompanyForm(false)} className="text-emerald-500 hover:text-emerald-700 cursor-pointer">✕</button>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="label text-xs font-semibold text-emerald-900">Company Name <span className="text-red-500">*</span></label>
                    {quickCompanyName.trim() && (
                      <span className="text-[11px] font-mono font-semibold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded border border-emerald-200">
                        Auto Code: {generateSmartShortCodeCandidates(quickCompanyName).find((c) => !companies.some((comp) => comp.short_code.toUpperCase() === c.toUpperCase())) || 'NEW'}
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={quickCompanyName}
                    onChange={(e) => setQuickCompanyName(e.target.value)}
                    placeholder="Enter Company Name (e.g. Swastik Private Limited)"
                    className="input bg-white text-xs"
                    autoFocus
                  />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setShowQuickCompanyForm(false)} className="btn btn-secondary text-xs cursor-pointer">Cancel</button>
                  <button type="button" onClick={handleQuickCreateCompany} disabled={quickCompanySaving} className="btn btn-primary text-xs cursor-pointer">
                    {quickCompanySaving ? 'Creating…' : 'Create & Select'}
                  </button>
                </div>
              </div>
            )}

            {/* Inline Personnel Form for Competent Person */}
            {showInlineInspectorForm && (
              <div className="p-4 bg-brand-50/90 border border-brand-200 rounded-xl shadow-sm space-y-3">
                <InlinePersonnelForm
                  initialRole="competent_person"
                  initialFullName={activeInspectorInitialName}
                  title="Create New Competent Person / Inspector"
                  onCreated={handleInspectorCreated}
                  onCancel={() => {
                    setShowInlineInspectorForm(false);
                    setActiveInspectorTargetRowId(null);
                    setActiveInspectorInitialName('');
                  }}
                />
              </div>
            )}

            {/* Inline Equipment Type Form */}
            {showInlineTypeForm && (
              <div className="p-4 bg-indigo-50/90 border border-indigo-200 rounded-xl shadow-sm space-y-3">
                <InlineEquipmentTypeForm
                  onCreated={handleTypeCreated}
                  onCancel={() => {
                    setShowInlineTypeForm(false);
                    setActiveTypeTargetRowId(null);
                  }}
                />
              </div>
            )}

            {/* Inline Test Method Form */}
            {showInlineTestMethodForm && (
              <div className="p-4 bg-blue-50/90 border border-blue-200 rounded-xl shadow-sm space-y-3">
                <InlineTestMethodForm
                  onCreated={handleTestMethodCreated}
                  onCancel={() => {
                    setShowInlineTestMethodForm(false);
                    setActiveTestMethodTargetRowId(null);
                  }}
                />
              </div>
            )}

            {/* Step 1: Upload File area if no parsed rows yet */}
            {parsedRows.length === 0 ? (
              <>
                <div className="border-2 border-dashed border-slate-300 hover:border-brand-500 rounded-2xl p-8 text-center transition-colors bg-white">
                  <input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileUpload}
                    id="form10-excel-file-upload-input"
                    className="hidden"
                  />
                  <label htmlFor="form10-excel-file-upload-input" className="cursor-pointer block space-y-3">
                    <div className="w-14 h-14 bg-brand-50 text-brand-600 rounded-2xl flex items-center justify-center mx-auto text-2xl shadow-sm">
                      📁
                    </div>
                    <div>
                      <span className="text-base font-bold text-slate-800 hover:text-brand-600 underline">
                        Click to browse & upload Excel file
                      </span>
                      <span className="text-slate-500 text-sm block mt-1">or drag and drop `.xlsx` / `.csv` file here</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Supports any Excel sheet (matches Company Short Code or Company Name, Equipment Type & Inspector Name automatically!)
                    </p>
                  </label>
                  {parsing && (
                    <p className="text-xs font-semibold text-brand-600 mt-4 flex items-center justify-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-brand-600 border-t-transparent rounded-full" />
                      Parsing Excel spreadsheet data...
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <div className="text-xs text-slate-500 font-medium">
                    Upload Excel file to begin
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      handleResetModal();
                      onClose();
                    }}
                    className="btn btn-secondary text-xs cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              /* Step 2: Interactive Table & Review */
              <>
                <div className="space-y-3">
                  {/* File Info Bar */}
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 bg-brand-100 text-brand-700 rounded-lg flex items-center justify-center font-bold text-xs shrink-0">📄</span>
                      <div>
                        <span className="font-bold text-slate-900 text-sm">
                          Parsed {parsedRows.length} Row(s) from <span className="text-brand-700 font-mono">{fileName}</span>
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleResetModal}
                      className="px-3 py-1 bg-white border border-slate-200 text-slate-600 hover:text-slate-900 rounded-lg text-xs font-semibold hover:bg-slate-50 shadow-sm transition-all cursor-pointer"
                    >
                      Upload Different File
                    </button>
                  </div>

                  {/* Batch Actions Toolbar (Sleek 1-Line Flex Layout) */}
                  <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm flex flex-wrap items-center gap-3 text-xs">
                    <span className="font-bold text-slate-800 shrink-0 flex items-center gap-1.5">
                      <span className="text-amber-500 text-sm">⚡</span>
                      <span>Batch Apply:</span>
                    </span>

                    {/* Batch Company */}
                    <div className="flex items-center gap-1.5 min-w-[240px]">
                      <div className="flex-1">
                        <AsyncSearchableSelect
                          value={batchCompanyId}
                          onChange={(val) => setBatchCompanyId(val)}
                          fetchOptions={fetchCompanyOptions}
                          placeholder="Select Company…"
                          isCompanySelect={true}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={applyBatchCompany}
                        disabled={!batchCompanyId}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply All
                      </button>
                    </div>

                    {/* Batch Equipment Type */}
                    <div className="flex items-center gap-1.5 min-w-[200px]">
                      <div className="flex-1">
                        <SearchableSelect
                          value={batchEquipmentType}
                          onChange={(val) => setBatchEquipmentType(val)}
                          options={allTypeOptions}
                          placeholder="Select Type…"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={applyBatchEquipmentType}
                        disabled={!batchEquipmentType}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply All
                      </button>
                    </div>

                    {/* Batch Inspector */}
                    <div className="flex items-center gap-1.5">
                      <select
                        value={batchPersonnelId}
                        onChange={(e) => setBatchPersonnelId(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium max-w-[170px]"
                      >
                        <option value="">-- Inspector --</option>
                        {inspectors.map((ins) => (
                          <option key={ins.id} value={ins.id}>
                            {ins.full_name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={applyBatchPersonnel}
                        disabled={!batchPersonnelId}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply All
                      </button>
                    </div>

                    {/* Batch Date */}
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        value={batchDate}
                        onChange={(e) => setBatchDate(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium"
                      />
                      <button
                        type="button"
                        onClick={applyBatchDate}
                        disabled={!batchDate}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply Date
                      </button>
                    </div>

                    {/* Batch Test Method */}
                    <div className="flex items-center gap-1.5">
                      <select
                        value={batchTestMethod}
                        onChange={(e) => setBatchTestMethod(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium max-w-[200px]"
                      >
                        {allTestMethodOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={applyBatchTestMethod}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply Method
                      </button>
                    </div>

                    {/* Batch Defect Status */}
                    <div className="flex items-center gap-1.5">
                      <select
                        value={batchDefectStatus}
                        onChange={(e) => setBatchDefectStatus(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium"
                      >
                        <option value="SAFE">Safe</option>
                        <option value="REJECT">Reject</option>
                        <option value="NOT_APPLICABLE">Not Applicable</option>
                      </select>
                      <button
                        type="button"
                        onClick={applyBatchDefectStatus}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply Status
                      </button>
                    </div>
                  </div>

                  {/* Warning Banner for Multiple Companies matching same name */}
                  {multipleMatchRowsCount > 0 && (
                    <div className="p-3.5 bg-amber-50 border border-amber-300 text-amber-950 rounded-xl text-xs flex items-start gap-3 shadow-sm">
                      <span className="text-amber-600 font-bold text-lg shrink-0">⚠️</span>
                      <div className="space-y-1">
                        <h4 className="font-bold text-amber-900 text-xs sm:text-sm">
                          Friendly Note: Multiple Companies Found with Matching Name ({multipleMatchRowsCount} Row{multipleMatchRowsCount > 1 ? 's' : ''})
                        </h4>
                        <p className="text-amber-800 text-[11px] sm:text-xs">
                          The company name in your Excel matches <strong>multiple companies</strong> in the system for {multipleMatchRowsCount} row{multipleMatchRowsCount > 1 ? 's' : ''}.
                          The first match was auto-selected. <strong>Please validate that the selected company is correct before proceeding.</strong>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Validation Info Banner */}
                  {!validationSummary.isValid && (
                    <div className="p-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-xl text-xs flex items-center justify-between">
                      <span className="font-semibold">⚠️ Action Required: {validationSummary.reason}</span>
                      <span className="text-[11px] text-amber-700">Satisfy all mandatory fields to activate import button</span>
                    </div>
                  )}

                  {/* Interactive Staging Table */}
                  <div className="max-h-[50vh] overflow-y-auto overflow-x-auto border border-slate-200 rounded-xl shadow-inner bg-white">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="sticky top-0 bg-slate-100 border-b border-slate-200 z-10 font-bold text-slate-700">
                        <tr>
                          <th className="py-2.5 px-3 w-10 text-center">#</th>
                          <th className="py-2.5 px-3 min-w-[280px]">Company Selection <span className="text-red-500">*</span></th>
                          <th className="py-2.5 px-3 min-w-[220px]">Equipment Type <span className="text-red-500">*</span></th>
                          <th className="py-2.5 px-3 min-w-[140px]">Tag Name</th>
                          <th className="py-2.5 px-3 min-w-[90px]">SWL (T)</th>
                          <th className="py-2.5 px-3 min-w-[200px]">Competent Person <span className="text-red-500">*</span></th>
                          <th className="py-2.5 px-3 min-w-[130px]">Exam Date <span className="text-red-500">*</span></th>
                          <th className="py-2.5 px-3 min-w-[220px]">Test Method</th>
                          <th className="py-2.5 px-3 min-w-[120px]">Defect Status</th>
                          <th className="py-2.5 px-3 min-w-[160px]">Defect Remarks</th>
                          <th className="py-2.5 px-3 min-w-[150px]">Status & Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {parsedRows.map((row, idx) => (
                          <tr key={row.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-2 px-3 text-center text-slate-400 font-mono text-[11px]">{idx + 1}</td>

                            {/* Company Dropdown + + New Company */}
                            <td className="py-2 px-3 space-y-1">
                              <div className="flex items-center gap-1">
                                <div className="flex-1">
                                  <AsyncSearchableSelect
                                    value={row.company_id}
                                    onChange={(val) => updateRow(row.id, { company_id: val })}
                                    fetchOptions={fetchCompanyOptions}
                                    placeholder="Search company…"
                                    isCompanySelect={true}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveCompanyTargetRowId(row.id);
                                    setQuickCompanyName(row.company_input || '');
                                    setShowQuickCompanyForm(true);
                                  }}
                                  className="px-1.5 py-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded shrink-0 cursor-pointer"
                                  title="Create New Company"
                                >
                                  + New Company
                                </button>
                              </div>
                              {row.has_multiple_company_matches ? (
                                <p className="text-[10px] text-amber-900 font-semibold bg-amber-50/90 p-1.5 rounded border border-amber-300 mt-1">
                                  ⚠️ <strong>Multiple Companies Found ({row.matching_companies_count} matches):</strong> Matched {row.matching_company_names?.join(', ')}. Please validate that this is the intended company.
                                </p>
                              ) : !row.company_id && row.company_input ? (
                                <p className="text-[10px] text-amber-700 font-medium">
                                  Unmatched Excel Company: "{row.company_input}" — Click + New Company to create
                                </p>
                              ) : null}
                            </td>

                            {/* Equipment Type Dropdown + + New Type */}
                            <td className="py-2 px-3 space-y-1">
                              <div className="flex items-center gap-1">
                                <div className="flex-1">
                                  <SearchableSelect
                                    value={row.equipment_type}
                                    onChange={(val) => {
                                      const opt = allTypeOptions.find((o) => o.value === val);
                                      updateRow(row.id, {
                                        equipment_type: val,
                                        equipment_type_label: opt?.label || val,
                                      });
                                    }}
                                    options={allTypeOptions}
                                    placeholder="Select Type…"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveTypeTargetRowId(row.id);
                                    setShowInlineTypeForm(true);
                                  }}
                                  className="px-1.5 py-1 text-[11px] font-bold text-indigo-700 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded shrink-0 cursor-pointer"
                                  title="Create New Equipment Type"
                                >
                                  + New Type
                                </button>
                              </div>
                              {!row.equipment_type && row.equipment_type_input && (
                                <p className="text-[10px] text-amber-700 font-medium">
                                  Unmatched Type: "{row.equipment_type_input}" — Click + New Type to create
                                </p>
                              )}
                            </td>

                            {/* Tag Name */}
                            <td className="py-2 px-3">
                              <input
                                type="text"
                                value={row.equipment_short_code}
                                onChange={(e) => updateRow(row.id, { equipment_short_code: e.target.value.toUpperCase() })}
                                className="input text-xs py-1 px-2 font-mono uppercase bg-white w-full"
                                placeholder="(Auto-generated)"
                              />
                            </td>

                            {/* SWL (Ton) */}
                            <td className="py-2 px-3">
                              <input
                                type="number"
                                step="0.1"
                                value={row.swl_value || ''}
                                onChange={(e) => updateRow(row.id, { swl_value: e.target.value })}
                                className="input text-xs py-1 px-2 bg-white w-20"
                                placeholder="e.g. 5"
                              />
                            </td>

                            {/* Competent Person Dropdown + + New Inspector */}
                            <td className="py-2 px-3 space-y-1">
                              <div className="flex items-center gap-1">
                                <select
                                  value={row.personnel_id}
                                  onChange={(e) => updateRow(row.id, { personnel_id: e.target.value })}
                                  className={`input text-xs py-1 px-2 font-medium flex-1 ${!row.personnel_id ? 'border-amber-300 bg-amber-50/50' : 'bg-white'}`}
                                >
                                  <option value="">-- Select Inspector --</option>
                                  {inspectors.map((ins) => (
                                    <option key={ins.id} value={ins.id}>
                                      {ins.full_name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveInspectorTargetRowId(row.id);
                                    setActiveInspectorInitialName(row.personnel_input || '');
                                    setShowInlineInspectorForm(true);
                                  }}
                                  className="px-1.5 py-1 text-[11px] font-bold text-brand-700 hover:text-brand-900 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded shrink-0 cursor-pointer"
                                  title="Create New Inspector"
                                >
                                  + New Inspector
                                </button>
                              </div>
                              {!row.personnel_id && row.personnel_input && (
                                <p className="text-[10px] text-amber-700 font-medium">
                                  Unmatched Inspector: "{row.personnel_input}" — Click + New Inspector to create
                                </p>
                              )}
                            </td>

                            {/* Exam Date */}
                            <td className="py-2 px-3">
                              <input
                                type="date"
                                value={row.examination_date}
                                onChange={(e) => updateRow(row.id, { examination_date: e.target.value })}
                                className="input text-xs py-1 px-1.5 bg-white font-medium"
                                required
                              />
                            </td>

                            {/* Test Method */}
                            <td className="py-2 px-3 space-y-1">
                              <div className="flex items-center gap-1">
                                <select
                                  value={row.test_method}
                                  onChange={(e) => updateRow(row.id, { test_method: e.target.value })}
                                  className="input text-xs py-1 px-1.5 bg-white font-medium flex-1 min-w-[170px]"
                                >
                                  {allTestMethodOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveTestMethodTargetRowId(row.id);
                                    setShowInlineTestMethodForm(true);
                                  }}
                                  className="px-1.5 py-1 text-[11px] font-bold text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded shrink-0 cursor-pointer"
                                  title="Create New Test Method"
                                >
                                  + Method
                                </button>
                              </div>
                            </td>

                            {/* Defect Status */}
                            <td className="py-2 px-3">
                              <select
                                value={row.defect_status}
                                onChange={(e) => updateRow(row.id, { defect_status: e.target.value as ParsedForm10Row['defect_status'] })}
                                className={`input text-xs py-1 px-1.5 font-medium ${row.defect_status === 'REJECT' ? 'text-red-700 font-bold bg-red-50' : 'bg-white'}`}
                              >
                                <option value="SAFE">Safe</option>
                                <option value="REJECT">Reject</option>
                                <option value="NOT_APPLICABLE">Not Applicable</option>
                              </select>
                            </td>

                            {/* Defect Remarks */}
                            <td className="py-2 px-3">
                              {row.defect_status === 'REJECT' ? (
                                <input
                                  type="text"
                                  value={row.defect_remarks || ''}
                                  onChange={(e) => updateRow(row.id, { defect_remarks: e.target.value })}
                                  className="input text-xs py-1 px-2 bg-red-50/40 border-red-300 w-full"
                                  placeholder="Defect details (Point 8)"
                                />
                              ) : (
                                <span className="text-slate-400 text-[11px] italic">—</span>
                              )}
                            </td>

                            {/* Status & Actions */}
                            <td className="py-2 px-3">
                              <div className="flex items-center justify-between gap-1 flex-wrap">
                                {row.has_multiple_company_matches && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-300" title="Multiple companies found with this name. Please validate that the correct company is selected.">
                                    ⚠️ Please Validate
                                  </span>
                                )}
                                {row.status === 'VALID' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                    ✓ Ready
                                  </span>
                                )}
                                {row.status === 'MISSING_COMPANY' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                {row.status === 'MISSING_EQUIPMENT_TYPE' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                {row.status === 'MISSING_INSPECTOR' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                {row.status === 'INVALID' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeRow(row.id)}
                                  className="text-slate-400 hover:text-red-600 font-bold p-1 cursor-pointer"
                                  title="Remove Row"
                                >
                                  ✕
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Progress Bar during execution */}
                  {progress && (
                    <div className="p-4 bg-brand-50 border border-brand-200 rounded-xl space-y-2">
                      <div className="flex items-center justify-between text-xs font-bold text-brand-900">
                        <span>{progress.step}</span>
                        <span>{progress.current} / {progress.total}</span>
                      </div>
                      <div className="w-full bg-brand-200 rounded-full h-2.5 overflow-hidden">
                        <div
                          className="bg-brand-600 h-2.5 rounded-full transition-all duration-200"
                          style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom Modal Actions Bar */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <div className="text-xs text-slate-500 font-medium flex items-center gap-2">
                    <span>{parsedRows.length > 0 ? `${parsedRows.length} certificate(s) ready` : 'Upload Excel file to begin'}</span>
                    {!validationSummary.isValid && parsedRows.length > 0 && (
                      <span className="text-amber-700 font-semibold bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-[11px]">
                        ⚠️ {validationSummary.reason}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        handleResetModal();
                        onClose();
                      }}
                      disabled={importing}
                      className="btn btn-secondary text-xs"
                    >
                      Cancel
                    </button>
                    {parsedRows.length > 0 && (
                      <button
                        type="button"
                        onClick={handleExecuteImport}
                        disabled={importing || !validationSummary.isValid}
                        className={`btn text-xs px-5 py-2 flex items-center gap-1.5 font-semibold transition-all ${
                          validationSummary.isValid && !importing
                            ? 'bg-brand-600 hover:bg-brand-700 text-white shadow-md cursor-pointer'
                            : 'bg-slate-300 text-slate-500 cursor-not-allowed opacity-70'
                        }`}
                        title={!validationSummary.isValid ? validationSummary.reason : 'Import Form 10 certificates'}
                      >
                        {importing ? (
                          <>
                            <span className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                            Importing Certificates…
                          </>
                        ) : (
                          <>
                            🚀 Confirm & Import {parsedRows.length} Form 10 Certificates
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
